import React, { useMemo } from "react";
import { ActionMenu } from "Components";
import { PERMISSION } from "Constants";
import { useAllowedTo, useDialog, useLoginInfo } from "Hooks";
import { ActionItem, Member } from "Types";
import EditGroupRole from "../EditGroupRole/EditGroupRole";
import RemoveGroupMember from "./RemoveGroupMember";

type GroupMemberActionsProps = {
  member: Member;
  groupId: string;
};

const GroupMemberActions = ({
  member,
  groupId,
}: GroupMemberActionsProps): React.JSX.Element => {
  const { isLocalLogin } = useLoginInfo();

  const findCurrentGroupInMember = useMemo(
    () => member.org_groups.find((item) => item.group_id === groupId),
    [groupId, member],
  );

  const editDialog = useDialog();
  const removeDialog = useDialog();

  // we need to pass the current row group id when checking permissions for these actions
  const canEdit = useAllowedTo({
    permissions: [PERMISSION.MODIFY_MEMBERS_GROUP_ROLE],
    groupId,
  });

  const hasRemoveMemberPermission = useAllowedTo({
    permissions: [PERMISSION.REMOVE_MEMBER_FROM_GROUP],
    groupId,
  });

  const canRemoveMember = hasRemoveMemberPermission && isLocalLogin;

  const actions: ActionItem[] = [];

  if (canEdit)
    actions.push({
      text: "Edit group role",
      type: "EDIT",
      onClick: editDialog.open,
    });

  if (canRemoveMember)
    actions.push({
      text: "Remove from group",
      type: "DELETE",
      onClick: removeDialog.open,
    });

  return (
    <>
      <div>
        <ActionMenu items={actions} />
      </div>
      {findCurrentGroupInMember && (
        <EditGroupRole
          user={member.email}
          memberId={member.user_id}
          currentRole={findCurrentGroupInMember.role}
          groupId={groupId}
          isOpen={editDialog.isOpen}
          onClose={editDialog.close}
        />
      )}
      <RemoveGroupMember
        member={member}
        groupId={groupId}
        isOpen={removeDialog.isOpen}
        onClose={removeDialog.close}
      />
    </>
  );
};

export default GroupMemberActions;
